import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { useId, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  apiPutRegistrationFinishPatient,
  apiPutRegistrationFinishPhysician,
  apiPutUsersResetPassword,
} from "../../../api";
import { RequestError } from "../../../api/api-connector";
import { genericErrorMsg } from "../../../app/common-messages";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { PasswordInput } from "../../../components/ui-kit/fields/password-input";
import { Form } from "../../../components/ui-kit/form/form";
import { SuccessMessage } from "../../../components/ui-kit/success-message";

export function ResetPasswordRoute(props: { mode: "reset" | "set" }) {
  const { t } = useLingui();
  const newPasswordId = useId();
  const repeatPasswordId = useId();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const passwordHint = t`Passwords must have at least 10 characters, including at least one uppercase letter, one lowercase letter, and one digit.`;

  const handleSubmit = async () => {
    setSuccessMessage("");
    setErrorMessage("");

    const token = searchParams.get("token");
    const expiryToken = searchParams.get("expiry_token");

    if (!token || !expiryToken) {
      setErrorMessage(
        t`Invalid password reset link. Missing token parameters.`,
      );
      return;
    }

    const body = {
      token,
      expiry_token: expiryToken,
      new_password: newPassword,
      repeat_password: repeatPassword,
    };

    try {
      if (props.mode === "set") {
        const type = searchParams.get("type");
        if (type === "physician") {
          await apiPutRegistrationFinishPhysician({ body });
        } else if (type === "patient") {
          await apiPutRegistrationFinishPatient({ body });
        } else {
          setErrorMessage(t`Invalid registration link. Missing user type.`);
          return;
        }
      } else {
        await apiPutUsersResetPassword({ body });
      }

      setSuccessMessage(
        props.mode === "set"
          ? t`Your password has been set successfully.`
          : t`Your password has been reset successfully.`,
      );
    } catch (error) {
      if (error instanceof RequestError) {
        if (error.response.status === 406) {
          setErrorMessage(
            t`This password reset link is invalid, expired, or has already been used. Please request a new one.`,
          );
        } else if (error.response.status === 422) {
          setErrorMessage(t`Password validation failed.` + " " + passwordHint);
        } else {
          setErrorMessage(t(genericErrorMsg));
        }
      } else {
        throw error;
      }
    }
  };

  return (
    <div className="grow flex items-center justify-center">
      <div className="flex flex-col w-80">
        <h2 className="text-xl font-semibold mb-4">
          {props.mode === "set" ? (
            <Trans>Set password</Trans>
          ) : (
            <Trans>Reset password</Trans>
          )}
        </h2>

        <p className="text-muted-foreground mb-6 text-sm">{passwordHint}</p>

        {successMessage ? (
          <>
            <SuccessMessage message={successMessage} />

            <div className="mt-6">
              <Link
                to="/login"
                className="text-xs text-brand-primary hover:underline"
              >
                <Trans>Back to login</Trans>
              </Link>
            </div>
          </>
        ) : (
          <Form className="flex flex-col" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label htmlFor={newPasswordId}>
                  <Trans>New password:</Trans>
                </label>
                <PasswordInput
                  id={newPasswordId}
                  value={newPassword}
                  onChange={(newValue) => {
                    setNewPassword(newValue);
                  }}
                  required
                  autofocus
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={repeatPasswordId}>
                  <Trans>Repeat password:</Trans>
                </label>
                <PasswordInput
                  id={repeatPasswordId}
                  value={repeatPassword}
                  onChange={(newValue) => {
                    setRepeatPassword(newValue);
                  }}
                  required
                />
              </div>
            </div>

            <ErrorMessage message={errorMessage} />

            <div className="flex items-center justify-between mt-6">
              <Link
                to="/login"
                className="text-xs text-brand-primary hover:underline"
              >
                <Trans>Back to login</Trans>
              </Link>

              <Button variant="primary" type="submit">
                {props.mode === "set" ? (
                  <Trans context="button">Set password</Trans>
                ) : (
                  <Trans context="button">Reset password</Trans>
                )}
              </Button>
            </div>
          </Form>
        )}
      </div>
    </div>
  );
}
